<template>
  <field :label="label" :empty="modelValue === undefined || modelValue === null" :variant="variant">
    <multiselect
      ref="multiselectRef"
      class="multi-select"
      :class="{ 'multi-select-extra-option': extraOption }"
      :open-direction="openDirection"
      v-bind="{ ...$attrs, variant: undefined }"
      :label-prop="labelProp"
      :variant="variant"
      :options="optionsFunction ?? options"
      :label="labelProp ?? 'label'"
      :model-value="modelValue ?? null"
    />
  </field>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import Multiselect from '@vueform/multiselect';
import Field, { type Variant } from '@components/utils/inputs/Field.vue';
import type { VueInstance } from '@vueuse/core';
import type { Option } from '@utils/filter/filterTypes/Dropdown.vue';
import { useI18n } from 'vue-i18n';
import { findAllScrollParents } from '@/utils/elements';

interface Props {
  labelProp?: string;
  label?: string;
  variant?: Variant;
  options?: unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  modelValue?: any;
  extraOption?: Option;
}

const props = defineProps<Props>();

const multiselectRef = ref<VueInstance | null>(null);
const openDirection = ref<'bottom' | 'top'>('bottom');
const scrollParents = computed(() => findAllScrollParents(multiselectRef.value?.$el));

const optionsFunction = ref();

const { t } = useI18n();

if (props.extraOption && typeof props.options === 'function') {
  optionsFunction.value = async (query: string, select: any) => {
    const input = query || select.modelValue;
    if (!input || input === props.extraOption.value) {
      return [
        ...[props.extraOption],
        {
          label: t('inputSearchString'),
          value: '-1',
          disabled: true,
        },
      ];
    }
    const origOptions = await props.options(query, select);

    return [
      ...[props.extraOption],
      ...origOptions,
    ];
  };
}

defineExpose({
  select(...args: unknown[]) {
    (multiselectRef.value as any)?.select(...args);
  },
  refreshOptions(...args: unknown[]) {
    (multiselectRef.value as any)?.refreshOptions(...args);
  },
});

onMounted(() => {
  for (const parent of scrollParents.value) {
    parent?.addEventListener('scroll', () => {
      const boundingRect = multiselectRef.value?.$el.getBoundingClientRect();
      openDirection.value = window.innerHeight - (boundingRect?.bottom ?? 0) > 200 ? 'bottom' : 'top';
    });
  }
});
</script>

<style src="@vueform/multiselect/themes/default.css"></style>
<style lang="scss" scoped>
.multi-select {
  /* Multiselect */
  --ms-option-bg-selected: var(--color-info-blue-100);
  --ms-option-bg-selected-pointed: var(--color-info-blue-100);
  --ms-ring-color: none;
  --ms-bg: transparent;
  --ms-px: #{px(20)};
  --ms-ring-width: 0px;
  --ms-border-width: 0px;
  --ms-placeholder-color: var(--color-gray-300);
  --ms-line-height: px(20);

  height: px(42);

  /*
  --ms-dropdown-bg: var(--color-gray-400);
  --ms-dropdown-radius: 18px;

  --ms-option-bg-selected: var(--color-gray-300);
  --ms-option-bg-selected-pointed: var(--color-gray-300);
  --ms-option-color-selected: var(--color-gray-100);
  --ms-option-color-selected-pointed: var(--color-gray-100);
  */

  --ms-option-color-disabled: var(--color-body-text);
}

.multi-select-extra-option:deep(.multiselect-option:first-child) {
  border-bottom: 1px solid var(--color-input-divider);
}

.multiselect {
  width: fit-content;
  min-width: px(180);
  margin: initial;
  padding: var(--input-padding, 0.63rem 0.88rem);
}
</style>
